/** The visitables a hero does business at, rather than walks past.
 *
 * A `bonus` object whose payload names a `service` is a counter: it asks a
 * question, takes payment and hands something over. `objects.ts` offers this
 * branch first and falls through to the ordinary stat grants when `service` is
 * absent, which is what lets one def do both - a Warcraft Keep that also gives
 * morale would just carry both keys.
 *
 * Every one of these was an empty payload before: fourteen defs the generator
 * placed, the renderer drew, and visiting did nothing at all. That is the
 * defect this file closes, and it is why the shape here is deliberately narrow
 * - each service reads its own keys off the payload, and none of them branches
 * on an object id (D9).
 *
 * ## Payload vocabulary
 *
 * | service     | keys                                                        |
 * |-------------|-------------------------------------------------------------|
 * | observatory | `scout` - radius of map lit, once per hero                   |
 * | cartographer| `price`, `waterOnly` - buys the whole plane                   |
 * | university  | `skills` (comma list), `price` - buys one secondary skill     |
 * | tree        | `price`, `priceResource`, `levels` - buys a level             |
 * | hillfort    | -                    - upgrades one stack, tier-scaled price  |
 * | shipyard    | `gold`, `wood`, `boat` - launches a hull on adjacent water    |
 * | warfactory  | `machines` (`"id:price,..."`) - sells a war machine           |
 * | market      | -                    - counts as a Marketplace once visited   |
 * | blackmarket | `offers` (`"id:price,..."`), rerolled weekly                  |
 * | altar       | -                    - trades a stack for experience          |
 * | leanto      | `min`, `max`         - one small resource, then empty for ever|
 * | wagon       | `min`, `max`, `artifactChanceBp` - artifact or resource, once |
 * | seer        | see `quests.ts`                                               |
 * | siren       | `lossPct` - the whole army pays, in experience                 |
 * | event       | `day`, `repeatDays`, `players`, `text` - the calendar, not a  |
 * |             | cell; see `applyTimedEvents`                                   |
 *
 * ## Prompts
 *
 * Every question raised here carries the resume sub `SVC_<service>`, which
 * `execute.ts` hands back to `resumeService`. The sub namespace is disjoint
 * from the kernel's own three (`FIGHT_OR_FLEE`, `TREASURE`, `PORTAL`) by the
 * prefix, so a new service can never shadow one of them.
 *
 * A prompt that cannot be raised - because one is already open - is not an
 * error and is never resolved by guessing: the service simply declines and
 * says so, because the alternative is spending a player's gold on a choice
 * they were never shown.
 */

import type { ArtifactDefId, CreatureDefId, SkillDefId } from "../ids.js";
import type { HeroId, ObjectId, PlayerId } from "../ids.js";
import { asArtifactDefId, asSkillDefId } from "./ids.js";
import type {
  ArmyState, HeroState, MapObjectState, ResourceKind, Resources,
} from "../state.js";
import { RESOURCES, cellIndex } from "../state.js";
import type { InteractionResponse } from "../state.js";
import { entityKeys } from "../util/iter.js";
import { indexOf, isWaterCell, occupantAt } from "../map/create.js";
import { revealAround } from "../map/fog.js";
import type { Ctx } from "./ctx.js";
import {
  OK_CANCEL, addResource, addResources, missingResource, mintObjectId,
  negate, optionResponses, patchHero, putObject, raise, rollBounded, rollRange,
} from "./ctx.js";
import { layFootprint } from "./create.js";
import type { Payload, VisitOutcome } from "./payload.js";
import {
  DONE, markPlayerVisit, payFlag, payList, payNum, payPair, payStr, payloadOf,
  playerVisited, removeObject, setPayload,
} from "./payload.js";
import {
  addCreatures, experienceForLevel, giveArtifact, grantExperience, hasFreeSlotFor,
  learnSkill, levelForExperience, setSlot, skillLevel, MAX_SECONDARY_SKILLS,
} from "./hero.js";
import { visitQuest, resumeQuest } from "./quests.js";
import { grantReward } from "./reward.js";
import type { UpgradeOffer } from "./upgrade.js";
import { FULL_PRICE_BP, HILL_FORT_TIER_BP, upgradeOffers } from "./upgrade.js";

// ------------------------------------------------------------------- shape

/** The resume sub a service's prompt carries. */
export const serviceSub = (service: string): string => `SVC_${service}`;

/** The service a resume sub names, or "" when the sub is not one of ours. */
export const subService = (sub: string): string =>
  sub.startsWith("SVC_") ? sub.slice(4) : "";

const message = (c: Ctx, p: PlayerId, text: string): void => {
  c.events.push({ type: "Message", player: p, text });
};

/**
 * Ask, or decline in the open.
 *
 * Returns true when the question is on screen. When it is not - because the
 * kernel allows exactly one outstanding interaction and something else got
 * there first - the caller must NOT resolve the choice itself: taking a
 * player's gold for an option they never saw is worse than making them walk
 * back tomorrow, and it is silent, which is the whole bug class this file was
 * written against.
 */
function ask(
  c: Ctx, heroId: HeroId, owner: PlayerId, o: MapObjectState, service: string,
  text: string, options: readonly string[], path: readonly number[],
): boolean {
  const responses = options.length === 0 ? OK_CANCEL : optionResponses(options.length);
  const prompt = options.length === 0
    ? { kind: "CONFIRM" as const, text }
    : { kind: "CHOOSE" as const, text, options: [...options] };
  const raised = raise(c, owner, prompt, responses, {
    kind: "OBJECT",
    data: { sub: serviceSub(service), hero: heroId, object: o.id, path: [...path] },
  });
  if (!raised) message(c, owner, `${service}:busy`);
  return raised;
}

// ------------------------------------------------------------------ entry

/**
 * The service branch of a `bonus` visit, or null when the payload names none.
 *
 * Null and not `DONE`, because "there is no service here" and "the service
 * resolved to nothing" are different answers and only the first may fall
 * through to the stat grants.
 */
export function visitService(
  c: Ctx, heroId: HeroId, o: MapObjectState, pay: Payload, path: readonly number[],
): VisitOutcome | null {
  const service = payStr(pay, "service");
  if (service === "") return null;
  const h = c.s.heroes[heroId];
  if (h === undefined) return DONE;

  switch (service) {
    case "observatory": return observatory(c, h, o, pay);
    case "cartographer": return cartographer(c, heroId, h, o, pay, path);
    case "university": return university(c, heroId, h, o, pay, path);
    case "tree": return tree(c, heroId, h, o, pay, path);
    case "hillfort": return hillFort(c, heroId, h, o, path);
    case "transformer": return skeletonTransformer(c, h, pay);
    case "shipyard": return shipyard(c, heroId, h, o, pay, path);
    case "warfactory": return warFactory(c, heroId, h, o, pay, path);
    case "market": return market(c, h, o);
    case "blackmarket": return blackMarket(c, heroId, h, o, pay, path);
    case "altar": return altar(c, heroId, h, o, path);
    case "leanto": return leanTo(c, h, o, pay);
    case "wagon": return wagon(c, heroId, h, o, pay);
    case "siren": return sirens(c, heroId, h, o, pay);
    case "seer": return visitQuest(c, heroId, h, o, pay, path);
    case "event":
      // A timed event belongs to the calendar, not to the ground: it fires
      // from `applyTimedEvents` at the day boundary. Walking over one is not
      // an interaction, and saying so would be noise on every step.
      return DONE;
    default:
      // A service nobody implements is a content bug, not a player action, and
      // it must not be silent - the object would otherwise look identical to
      // the fourteen empty payloads this file replaced.
      message(c, h.owner, `service:unknown:${service}`);
      return DONE;
  }
}

/** Answer a service's prompt. Never moves the hero; `execute.ts` resumes the walk. */
export function resumeService(
  c: Ctx, heroId: HeroId, objectId: ObjectId, sub: string, response: InteractionResponse,
): void {
  const service = subService(sub);
  if (service === "") return;
  const h = c.s.heroes[heroId];
  const o = c.s.objects[objectId];
  if (h === undefined || o === undefined || o.removed) return;
  const pay = payloadOf(c, o);
  const index = response.kind === "OPTION" ? response.index : -1;
  const confirmed = response.kind === "OK";

  switch (service) {
    case "cartographer": if (confirmed) buyMap(c, h, o, pay); break;
    case "university": if (index >= 0) buySkill(c, heroId, h, o, pay, index); break;
    case "tree": if (confirmed) buyLevel(c, heroId, h, o, pay); break;
    case "hillfort": if (index >= 0) buyUpgrade(c, h, index); break;
    case "shipyard": if (confirmed) buyBoat(c, h, o, pay); break;
    case "warfactory": if (index >= 0) buyMachine(c, heroId, h, pay, index); break;
    case "blackmarket": if (index >= 0) buyOffer(c, heroId, h, o, pay, index); break;
    case "altar": if (index >= 0) sacrifice(c, heroId, h, index); break;
    case "seer": resumeQuest(c, heroId, h, o, pay, response); break;
    // A wandering stack's join offer. It is not a `service` on a `bonus` - it
    // is `monster()`'s - but it resumes through the same door, so the sub
    // namespace is shared and the price rides on the token.
    case "join":
      // The price is read back off the object rather than out of the resume
      // token, because `execute.ts` forwards five arguments to this function
      // and widening that call is somebody else's file. `joinPrice` is written
      // by `monster()` when it raises the prompt; it is a runtime key, never
      // authored, so it is not in any def and not in the payload whitelist.
      if (confirmed) resolveJoin(c, heroId, objectId, payNum(pay, "joinPrice", 0));
      break;
    default: break;
  }
}

// ------------------------------------------------------------- observatory

/**
 * Sentinel Spire, Beacon of Flame, Watch Tower: a disc of map, once.
 *
 * The mark is per player rather than per hero, because what an observatory
 * hands over is knowledge and a kingdom only has to learn it once. The def
 * still carries `oneVisitPerHero`, which stops the *same* hero paying the walk
 * twice; this stops a second hero re-lighting ground the first already lit and
 * calling it a result.
 */
function observatory(c: Ctx, h: HeroState, o: MapObjectState, pay: Payload): VisitOutcome {
  const radius = payNum(pay, "scout", 0);
  if (radius <= 0) return DONE;
  if (playerVisited(c, o, h.owner)) {
    message(c, h.owner, "observatory:already");
    return DONE;
  }
  markPlayerVisit(c, o.id, h.owner);
  lightDisc(c, h.owner, o.x, o.y, o.z, radius);
  message(c, h.owner, `observatory:${String(radius)}`);
  return DONE;
}

function lightDisc(
  c: Ctx, p: PlayerId, x: number, y: number, z: number, radius: number,
): void {
  const fog = c.s.fog[p];
  if (fog === undefined) return;
  const cells = revealAround(fog, x, y, z, radius, c.s.map);
  if (cells.length > 0) c.events.push({ type: "FogRevealed", player: p, cells });
}

// ------------------------------------------------------------ cartographer

/**
 * A thousand gold for the whole plane the hut stands on.
 *
 * Not the whole map: the classic sells three cartographies and each covers one thing,
 * and a hut that lit the underworld from the surface would hand a player the
 * second level before they had found a single gate into it. `waterOnly` is the
 * sea cartographer, which lights the water and leaves the land black.
 */
function cartographer(
  c: Ctx, heroId: HeroId, h: HeroState, o: MapObjectState, pay: Payload,
  path: readonly number[],
): VisitOutcome {
  if (playerVisited(c, o, h.owner)) {
    message(c, h.owner, "cartographer:already");
    return DONE;
  }
  const price = payNum(pay, "price", 0);
  const pl = c.s.players[h.owner];
  if (pl !== undefined && pl.resources.gold < price) {
    message(c, h.owner, `cartographer:poor:${String(price)}`);
    return DONE;
  }
  if (!ask(c, heroId, h.owner, o, "cartographer", `cartographer:${String(price)}`, [], path)) {
    return DONE;
  }
  return { kind: "pending" };
}

function buyMap(c: Ctx, h: HeroState, o: MapObjectState, pay: Payload): void {
  const price = payNum(pay, "price", 0);
  const pl = c.s.players[h.owner];
  if (pl === undefined || pl.resources.gold < price) return;
  addResource(c, h.owner, "gold", -price);
  markPlayerVisit(c, o.id, h.owner);

  const fog = c.s.fog[h.owner];
  if (fog === undefined) return;
  const map = c.s.map;
  const waterOnly = payFlag(pay, "waterOnly");
  const cells: number[] = [];
  for (let y = 0; y < map.height; y++) {
    for (let x = 0; x < map.width; x++) {
      const i = cellIndex(map, x, y, o.z);
      if (waterOnly !== isWaterCell(map, i)) continue;
      if (fog.seen[i] !== 2) cells.push(i);
      fog.seen[i] = 2;
    }
  }
  if (cells.length > 0) c.events.push({ type: "FogRevealed", player: h.owner, cells });
  message(c, h.owner, `cartographer:bought:${String(cells.length)}`);
}

// -------------------------------------------------------------- university

/** The skills a University still has anything to teach this hero. */
function teachable(c: Ctx, h: HeroState, pay: Payload): readonly SkillDefId[] {
  const out: SkillDefId[] = [];
  for (const raw of payList(pay, "skills")) {
    const id = asSkillDefId(raw);
    if (c.content.skills.get(id) === undefined) continue;
    // Basic only. A University teaches what a hero does not know; it does not
    // promote what they already have, which is what the level-up offer is for.
    // And a hero with eight skills has no room for a ninth: `learnSkill` is a
    // silent no-op at the cap, which used to take the gold and teach nothing.
    if (skillLevel(h, id) !== 0) continue;
    if (h.secondary.length >= MAX_SECONDARY_SKILLS) continue;
    if (!out.includes(id)) out.push(id);
  }
  return out;
}

function university(
  c: Ctx, heroId: HeroId, h: HeroState, o: MapObjectState, pay: Payload,
  path: readonly number[],
): VisitOutcome {
  const offers = teachable(c, h, pay);
  if (offers.length === 0) {
    message(c, h.owner, "university:nothing");
    return DONE;
  }
  const price = payNum(pay, "price", 0);
  const pl = c.s.players[h.owner];
  if (pl !== undefined && pl.resources.gold < price) {
    message(c, h.owner, `university:poor:${String(price)}`);
    return DONE;
  }
  const options = offers.map((s) => `${s as string}:${String(price)}`);
  if (!ask(c, heroId, h.owner, o, "university", "university", options, path)) return DONE;
  return { kind: "pending" };
}

function buySkill(
  c: Ctx, heroId: HeroId, h: HeroState, o: MapObjectState, pay: Payload, index: number,
): void {
  const offers = teachable(c, h, pay);
  const pick = offers[index];
  if (pick === undefined) return;
  const price = payNum(pay, "price", 0);
  const pl = c.s.players[h.owner];
  if (pl === undefined || pl.resources.gold < price) return;
  addResource(c, h.owner, "gold", -price);
  const fresh = c.s.heroes[heroId];
  if (fresh === undefined) return;
  learnSkill(c, fresh, pick);
  markPlayerVisit(c, o.id, h.owner);
  message(c, h.owner, `university:taught:${pick as string}`);
}

// ------------------------------------------------------------------- tree

/**
 * The Elder Oak: a level, for a price, once per hero.
 *
 * The grant is experience and not a level counter, because a level in this
 * kernel is a function of experience (`levelForExperience`) and writing one
 * without the other would make the two disagree at the next battle. Enough
 * experience to reach the next threshold is exactly one level, and the
 * level-up prompt that follows is the ordinary one.
 */
function tree(
  c: Ctx, heroId: HeroId, h: HeroState, o: MapObjectState, pay: Payload,
  path: readonly number[],
): VisitOutcome {
  const price = payNum(pay, "price", 0);
  const r = priceResource(pay);
  if (price > 0) {
    const pl = c.s.players[h.owner];
    if (pl !== undefined && pl.resources[r] < price) {
      message(c, h.owner, `tree:poor:${r}:${String(price)}`);
      return DONE;
    }
    if (!ask(c, heroId, h.owner, o, "tree", `tree:${r}:${String(price)}`, [], path)) return DONE;
    return { kind: "pending" };
  }
  grantLevels(c, heroId, h, payNum(pay, "levels", 1));
  return DONE;
}

const priceResource = (pay: Payload): ResourceKind => {
  const r = payStr(pay, "priceResource", "gold");
  return RESOURCES.includes(r as ResourceKind) ? (r as ResourceKind) : "gold";
};

function buyLevel(c: Ctx, heroId: HeroId, h: HeroState, o: MapObjectState, pay: Payload): void {
  const price = payNum(pay, "price", 0);
  const r = priceResource(pay);
  const pl = c.s.players[h.owner];
  if (pl === undefined || pl.resources[r] < price) return;
  addResource(c, h.owner, r, -price);
  markPlayerVisit(c, o.id, h.owner);
  grantLevels(c, heroId, h, payNum(pay, "levels", 1));
}

function grantLevels(c: Ctx, heroId: HeroId, h: HeroState, levels: number): void {
  const n = levels < 1 ? 1 : levels;
  const at = levelForExperience(c.content, h.experience);
  const need = experienceForLevel(c.content, at + n);
  const gap = need - h.experience;
  if (gap <= 0) {
    message(c, h.owner, "tree:capped");
    return;
  }
  // Deliberately not `grantExperience`: an experience *modifier* must not scale
  // a gift measured in levels, or a hero with Learning would get two levels out
  // of one tree and a hero without would sometimes get none.
  const total = h.experience + gap;
  patchHero(c, heroId, { experience: total });
  c.events.push({ type: "HeroGainedExperience", hero: heroId, amount: gap, total });
  message(c, h.owner, `tree:level:${String(at + n)}`);
}

// -------------------------------------------------------------- warcraft keep

/**
 * The upgrade price, per head, in the resources the two creatures cost.
 *
 * The classic's Warcraft Keep charges nothing for the bottom of an army and full price
 * for the top of it, which is what makes it worth walking a stack of spearwardens
 * across the map and not worth walking a stack of angels. The tier bands are
 * the rule; the numbers underneath are the two creatures' own costs.
 */
export { HILL_FORT_TIER_BP } from "./upgrade.js";

/**
 * A Warcraft Keep takes any upgrade the content defines, at its tier's discount.
 * The town equivalent lives in `town.ts` and differs only in the two arguments
 * below: it permits solely what its own dwellings produce, and pays full price.
 */
function fortOffers(c: Ctx, h: HeroState): readonly UpgradeOffer[] {
  const army = c.s.armies[h.army];
  if (army === undefined) return [];
  return upgradeOffers(
    c.content, army.slots,
    () => true,
    (from) => HILL_FORT_TIER_BP[from.tier] ?? FULL_PRICE_BP,
  );
}

function hillFort(
  c: Ctx, heroId: HeroId, h: HeroState, o: MapObjectState, path: readonly number[],
): VisitOutcome {
  const offers = fortOffers(c, h);
  if (offers.length === 0) {
    message(c, h.owner, "hillfort:nothing");
    return DONE;
  }
  const options = offers.map((u) =>
    `${u.to.id as string}:${String(u.count)}:${String(u.cost.gold ?? 0)}`);
  if (!ask(c, heroId, h.owner, o, "hillfort", "hillfort", options, path)) return DONE;
  return { kind: "pending" };
}

function buyUpgrade(c: Ctx, h: HeroState, index: number): void {
  const offers = fortOffers(c, h);
  const pick = offers[index];
  if (pick === undefined) return;
  const pl = c.s.players[h.owner];
  if (pl === undefined) return;
  const short = missingResource(pl.resources, pick.cost);
  if (short !== null) {
    message(c, h.owner, `hillfort:poor:${short.resource}:${String(short.need)}`);
    return;
  }
  addResources(c, h.owner, negate(pick.cost));
  setSlot(c, h.army, pick.slot, { def: pick.to.id, count: pick.count });
  message(c, h.owner, `hillfort:upgraded:${pick.to.id as string}:${String(pick.count)}`);
}

// --------------------------------------------------------------- shipyard

/**
 * A shipyard builds a hull; it does not ferry the hero onto it.
 *
 * The classic's shipyard is a purchase, and the boat then sits on the water until
 * somebody walks onto it. That matters here because boarding is the movement
 * loop's job and a hero standing at a shipyard is standing on land: putting him
 * straight onto the deck would skip the step that spends his movement.
 */
function shipyard(
  c: Ctx, heroId: HeroId, h: HeroState, o: MapObjectState, pay: Payload,
  path: readonly number[],
): VisitOutcome {
  const berth = freeBerth(c, o);
  if (berth === null) {
    message(c, h.owner, "shipyard:nowater");
    return DONE;
  }
  const cost = boatCost(pay);
  const pl = c.s.players[h.owner];
  if (pl !== undefined) {
    const short = missingResource(pl.resources, cost);
    if (short !== null) {
      message(c, h.owner, `shipyard:poor:${short.resource}:${String(short.need)}`);
      return DONE;
    }
  }
  const label = `shipyard:${String(cost.gold ?? 0)}:${String(cost.wood ?? 0)}`;
  if (!ask(c, heroId, h.owner, o, "shipyard", label, [], path)) return DONE;
  return { kind: "pending" };
}

const boatCost = (pay: Payload): Partial<Resources> => ({
  gold: payNum(pay, "gold", 1000),
  wood: payNum(pay, "wood", 10),
});

/**
 * The water cell a new hull is launched onto: free, unoccupied, and adjacent to
 * some cell of the shipyard's own footprint.
 *
 * Searched over the footprint rather than the anchor because a shipyard is a
 * two-by-two building whose anchor is inland by construction, and ties break on
 * the flat cell index so the berth is a function of the map and not of the
 * order the footprint happens to be authored in.
 */
function freeBerth(c: Ctx, o: MapObjectState): readonly [number, number] | null {
  const map = c.s.map;
  const footprint = c.content.mapObjects.get(o.def)?.footprint ?? [[0, 0]];
  let best: readonly [number, number] | null = null;
  let bestIndex = Number.MAX_SAFE_INTEGER;
  for (const [fx, fy] of footprint) {
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const x = o.x + fx + dx, y = o.y + fy + dy;
        const i = indexOf(map, x, y, o.z);
        if (i < 0 || !isWaterCell(map, i)) continue;
        if (occupantAt(map, i) !== null) continue;
        if (i < bestIndex) { bestIndex = i; best = [x, y]; }
      }
    }
  }
  return best;
}

function buyBoat(c: Ctx, h: HeroState, o: MapObjectState, pay: Payload): void {
  const berth = freeBerth(c, o);
  if (berth === null) return;
  const cost = boatCost(pay);
  const pl = c.s.players[h.owner];
  if (pl === undefined || missingResource(pl.resources, cost) !== null) return;

  const def = payStr(pay, "boat");
  const chosen = c.content.mapObjects.get(def)?.kind === "boat" ? def : smallestBoat(c);
  if (chosen === null) {
    message(c, h.owner, "shipyard:noboat");
    return;
  }
  addResources(c, h.owner, negate(cost));
  const hull: MapObjectState = {
    id: mintObjectId(c), kind: "boat", def: chosen,
    x: berth[0], y: berth[1], z: o.z,
    owner: h.owner, payload: {}, guard: null, visitedBy: [], removed: false,
  };
  putObject(c, hull);
  layFootprint(c, hull);
  message(c, h.owner, `shipyard:built:${chosen}`);
}

/** The one-cell hull, so a berth of one water cell is always enough. */
function smallestBoat(c: Ctx): string | null {
  let best: string | null = null;
  let bestCells = Number.MAX_SAFE_INTEGER;
  for (const id of [...c.content.mapObjects.keys()].sort()) {
    const def = c.content.mapObjects.get(id)!;
    if (def.kind !== "boat") continue;
    const cells = def.footprint.length === 0 ? 1 : def.footprint.length;
    if (cells < bestCells) { bestCells = cells; best = id; }
  }
  return best;
}

// ------------------------------------------------------------ war factory

/** The machines this hero has room for, with what each costs here. */
function machineOffers(
  c: Ctx, h: HeroState, pay: Payload,
): readonly { readonly art: ArtifactDefId; readonly price: number }[] {
  const out: { art: ArtifactDefId; price: number }[] = [];
  for (const entry of payList(pay, "machines")) {
    const { id, n } = payPair(entry);
    const art = asArtifactDefId(id);
    const def = c.content.artifacts.get(art);
    if (def === undefined) continue;
    // One of each. The slot is the test rather than the backpack, because a
    // second ballista in the bag fields nothing.
    const held = h.artifacts[def.slot];
    if (held !== null && held !== undefined) continue;
    out.push({ art, price: n > 0 ? n : def.cost });
  }
  return out;
}

function warFactory(
  c: Ctx, heroId: HeroId, h: HeroState, o: MapObjectState, pay: Payload,
  path: readonly number[],
): VisitOutcome {
  const offers = machineOffers(c, h, pay);
  if (offers.length === 0) {
    message(c, h.owner, "warfactory:nothing");
    return DONE;
  }
  const options = offers.map((m) => `${m.art as string}:${String(m.price)}`);
  if (!ask(c, heroId, h.owner, o, "warfactory", "warfactory", options, path)) return DONE;
  return { kind: "pending" };
}

function buyMachine(
  c: Ctx, heroId: HeroId, h: HeroState, pay: Payload, index: number,
): void {
  const pick = machineOffers(c, h, pay)[index];
  if (pick === undefined) return;
  const pl = c.s.players[h.owner];
  if (pl === undefined || pl.resources.gold < pick.price) {
    if (pl !== undefined) message(c, h.owner, `warfactory:poor:${String(pick.price)}`);
    return;
  }
  addResource(c, h.owner, "gold", -pick.price);
  equipMachine(c, heroId, pick.art);
  message(c, h.owner, `warfactory:bought:${pick.art as string}`);
}

/**
 * A war machine goes into its slot, not into the backpack.
 *
 * `giveArtifact` puts things in the bag and `EQUIP_ARTIFACT` moves them out
 * again, which for a machine is a second command the player has no reason to
 * be asked for: a bought ballista that sits unequipped fields nothing and looks
 * like the purchase failed.
 */
function equipMachine(c: Ctx, heroId: HeroId, art: ArtifactDefId): void {
  const h = c.s.heroes[heroId];
  const def = c.content.artifacts.get(art);
  if (h === undefined || def === undefined) return;
  const held = h.artifacts[def.slot];
  if (held === null || held === undefined) {
    patchHero(c, heroId, { artifacts: { ...h.artifacts, [def.slot]: art } });
    c.events.push({ type: "ArtifactGained", hero: heroId, artifact: art });
    return;
  }
  giveArtifact(c, heroId, art);
}

// ----------------------------------------------------------- trading post

/**
 * A Trading Post is a Marketplace the kingdom does not have to build.
 *
 * It is scored rather than executed: `tradeYield` reads how many Marketplaces a
 * player owns and nothing else, so the honest way to make a Trading Post narrow
 * the spread is to count as one. The mark is per player and permanent, which is
 * The classic reading - once your kingdom knows where the post is, it trades
 * through it.
 */
function market(c: Ctx, h: HeroState, o: MapObjectState): VisitOutcome {
  if (playerVisited(c, o, h.owner)) {
    message(c, h.owner, "market:already");
    return DONE;
  }
  markPlayerVisit(c, o.id, h.owner);
  message(c, h.owner, "market:open");
  return DONE;
}

/** Trading Posts this player has found. Added to the Marketplace count. */
export function tradingPosts(c: Ctx, p: PlayerId): number {
  let n = 0;
  for (const id of entityKeys(c.s.objects)) {
    const o = c.s.objects[id]!;
    if (o.removed || o.kind !== "bonus") continue;
    const pay = payloadOf(c, o);
    if (payStr(pay, "service") !== "market") continue;
    if (playerVisited(c, o, p)) n++;
  }
  return n;
}

// ----------------------------------------------------------- black market

/**
 * The stock, rerolled every week by `weeklyObjectReset`.
 *
 * Held in the object's own payload rather than derived on the spot, because two
 * heroes who walk in on the same day must see the same window - and because a
 * player who declines the price on Monday and comes back on Tuesday should find
 * the same artifact still sitting there.
 */
export const BLACK_MARKET_SLOTS = 4;
/**
 * The market's price as a multiple of the artifact's own cost, in basis
 * points: 15000 is one and a half times, so the market adds half again.
 */
export const BLACK_MARKET_MARKUP_BP = 15000;

export function rollBlackMarket(c: Ctx, id: ObjectId): void {
  const ids = [...c.content.artifacts.keys()].sort()
    .filter((a) => {
      const def = c.content.artifacts.get(a)!;
      return def.cost > 0 && def.slot !== "spellbook" && !def.slot.startsWith("warMachine");
    });
  if (ids.length === 0) return;
  const picked: string[] = [];
  for (let k = 0; k < BLACK_MARKET_SLOTS && picked.length < ids.length; k++) {
    // Rejection rather than a shuffle: the stock is four of a hundred and
    // fifty, so a redraw is cheap and a shuffle would advance the stream by the
    // whole catalogue every Monday of every game.
    for (let tries = 0; tries < 8; tries++) {
      const a = ids[rollBounded(c, "map.treasure", ids.length)]!;
      if (picked.some((p) => p.startsWith(`${a as string}:`))) continue;
      const cost = c.content.artifacts.get(a)!.cost;
      picked.push(`${a as string}:${String(Math.trunc((cost * BLACK_MARKET_MARKUP_BP) / 10000))}`);
      break;
    }
  }
  setPayload(c, id, { offers: picked.join(","), stocked: true });
}

function blackMarket(
  c: Ctx, heroId: HeroId, h: HeroState, o: MapObjectState, pay: Payload,
  path: readonly number[],
): VisitOutcome {
  // `stocked` and not `offers === ""` is the test, because "never opened" and
  // "sold out" are the same empty string and only the first may restock. With
  // the string alone, buying the last of four relics restocked the window on
  // the spot and the market became an unlimited armoury for whoever had the
  // gold.
  if (!payFlag(pay, "stocked")) {
    rollBlackMarket(c, o.id);
    return blackMarket(c, heroId, h, o, payloadOf(c, c.s.objects[o.id]!), path);
  }
  const options = payList(pay, "offers");
  if (options.length === 0) {
    message(c, h.owner, "blackmarket:empty");
    return DONE;
  }
  if (!ask(c, heroId, h.owner, o, "blackmarket", "blackmarket", options, path)) return DONE;
  return { kind: "pending" };
}

function buyOffer(
  c: Ctx, heroId: HeroId, h: HeroState, o: MapObjectState, pay: Payload, index: number,
): void {
  const offers = payList(pay, "offers");
  const entry = offers[index];
  if (entry === undefined) return;
  const { id, n } = payPair(entry);
  const art = asArtifactDefId(id);
  const def = c.content.artifacts.get(art);
  if (def === undefined) return;
  const price = n > 0 ? n : def.cost;
  const pl = c.s.players[h.owner];
  if (pl === undefined || pl.resources.gold < price) {
    message(c, h.owner, `blackmarket:poor:${String(price)}`);
    return;
  }
  addResource(c, h.owner, "gold", -price);
  giveArtifact(c, heroId, art);
  // Sold out of the window until Monday: the same relic must not be bought
  // twice out of the same stock.
  setPayload(c, o.id, { offers: offers.filter((_, i) => i !== index).join(",") });
  message(c, h.owner, `blackmarket:bought:${id}`);
}

// ------------------------------------------------------ altar of sacrifice

/**
 * Creatures for experience.
 *
 * The rate is the creature's own `aiValue` - this kernel's one answer to "what
 * is this creature worth" - times the number burned. Using `aiValue` rather
 * than a table of its own is what keeps the altar from becoming a second, and
 * eventually contradictory, statement of creature worth.
 *
 * A hero may not burn their last stack: an army of nothing cannot walk home,
 * and the elimination rule would take the hero with it.
 */
export const ALTAR_VALUE_BP = 10000;

interface SacrificeOffer {
  readonly slot: number;
  readonly def: CreatureDefId;
  readonly count: number;
  readonly experience: number;
}

function sacrificeOffers(c: Ctx, h: HeroState): readonly SacrificeOffer[] {
  const army = c.s.armies[h.army];
  if (army === undefined) return [];
  const filled = filledSlots(army);
  if (filled <= 1) return [];
  const out: SacrificeOffer[] = [];
  for (let i = 0; i < army.slots.length; i++) {
    const s = army.slots[i];
    if (s === null || s === undefined || s.count <= 0) continue;
    const def = c.content.creatures.get(s.def);
    if (def === undefined) continue;
    out.push({
      slot: i, def: s.def, count: s.count,
      experience: Math.trunc((def.aiValue * s.count * ALTAR_VALUE_BP) / 10000),
    });
  }
  return out;
}

const filledSlots = (a: ArmyState): number => {
  let n = 0;
  for (const s of a.slots) if (s !== null && s.count > 0) n++;
  return n;
};

function altar(
  c: Ctx, heroId: HeroId, h: HeroState, o: MapObjectState, path: readonly number[],
): VisitOutcome {
  const offers = sacrificeOffers(c, h);
  if (offers.length === 0) {
    message(c, h.owner, "altar:nothing");
    return DONE;
  }
  const options = offers.map((s) =>
    `${s.def as string}:${String(s.count)}:${String(s.experience)}`);
  if (!ask(c, heroId, h.owner, o, "altar", "altar", options, path)) return DONE;
  return { kind: "pending" };
}

/**
 * Bone Press: everything living in the army becomes bone.
 *
 * The expansion's rule is a conversion at equal total health, which is what makes it a
 * real decision rather than a free upgrade - forty peasants (40 hp) become six
 * skeletons and not forty. The undead are left alone: they are already bone,
 * and re-transforming them would launder a Carrion Wyrm into skeletons.
 *
 * Which creature the bones become is content's to name (`into` in the payload),
 * so the Saltreach variant that yields Carrion Wyrms is a data edit rather than a
 * second rule. Nothing here knows a creature id.
 */
function skeletonTransformer(c: Ctx, h: HeroState, pay: Payload): VisitOutcome {
  const intoId = payStr(pay, "into");
  const into = c.content.creatures.get(intoId as CreatureDefId);
  if (into === undefined || into.hp <= 0) return DONE;
  const army = c.s.armies[h.army];
  if (army === undefined) return DONE;

  let bones = 0;
  let taken = 0;
  for (let i = 0; i < army.slots.length; i++) {
    const slot = army.slots[i];
    if (slot === null || slot === undefined) continue;
    const def = c.content.creatures.get(slot.def);
    if (def === undefined) continue;
    // Already bone: left exactly as it is.
    if (def.abilities.some((a) => a.kind === "UNDEAD")) continue;
    bones += Math.floor((slot.count * def.hp) / into.hp);
    taken += slot.count;
    setSlot(c, h.army, i, null);
  }
  if (taken === 0) {
    message(c, h.owner, "transformer:nothing");
    return DONE;
  }
  if (bones > 0) addCreatures(c, h.army, into.id, bones);
  message(c, h.owner, `transformer:${String(taken)}:${String(bones)}`);
  return DONE;
}

function sacrifice(c: Ctx, heroId: HeroId, h: HeroState, index: number): void {
  const pick = sacrificeOffers(c, h)[index];
  if (pick === undefined) return;
  setSlot(c, h.army, pick.slot, null);
  grantExperience(c, heroId, pick.experience);
  message(c, h.owner, `altar:burned:${pick.def as string}:${String(pick.count)}`);
}

// --------------------------------------------------------- lean-to, wagon

/** The resources a small find can be made of. Gold is not one of them. */
const SMALL_RESOURCES: readonly ResourceKind[] =
  Object.freeze(RESOURCES.filter((r) => r !== "gold"));

/**
 * A Lean-To holds one small find, and once it is taken it is taken for ever.
 *
 * The emptiness is written into the payload rather than removing the object,
 * because the shack is still standing after you have gone through it - and a
 * player who walks a second hero past it should see a shack, not bare ground
 * with a pickup that used to be there.
 */
function leanTo(c: Ctx, h: HeroState, o: MapObjectState, pay: Payload): VisitOutcome {
  if (payFlag(pay, "emptied")) {
    message(c, h.owner, "leanto:empty");
    return DONE;
  }
  const r = SMALL_RESOURCES[rollBounded(c, "map.treasure", SMALL_RESOURCES.length)] ?? "wood";
  const amount = rollRange(c, "map.treasure", payNum(pay, "min", 1), payNum(pay, "max", 4));
  if (amount > 0) addResource(c, h.owner, r, amount);
  setPayload(c, o.id, { emptied: true });
  message(c, h.owner, `leanto:${r}:${String(amount)}`);
  return DONE;
}

/** A Wagon: usually an artifact, otherwise a handful of one resource. Once. */
function wagon(
  c: Ctx, heroId: HeroId, h: HeroState, o: MapObjectState, pay: Payload,
): VisitOutcome {
  if (payFlag(pay, "emptied")) {
    message(c, h.owner, "wagon:empty");
    return DONE;
  }
  setPayload(c, o.id, { emptied: true });
  const named = payStr(pay, "artifact");
  const bp = payNum(pay, "artifactChanceBp", 0);
  const wantsArtifact = named !== ""
    || (bp > 0 && rollBounded(c, "map.treasure", 10000) < bp);
  if (wantsArtifact) {
    const art = named !== "" ? asArtifactDefId(named) : cheapArtifact(c);
    if (art !== null && c.content.artifacts.get(art) !== undefined) {
      giveArtifact(c, heroId, art);
      message(c, h.owner, `wagon:artifact:${art as string}`);
      return DONE;
    }
  }
  const r = SMALL_RESOURCES[rollBounded(c, "map.treasure", SMALL_RESOURCES.length)] ?? "wood";
  const amount = rollRange(c, "map.treasure", payNum(pay, "min", 2), payNum(pay, "max", 5));
  if (amount > 0) addResource(c, h.owner, r, amount);
  message(c, h.owner, `wagon:${r}:${String(amount)}`);
  return DONE;
}

/** A treasure-class relic, drawn deterministically from the sorted catalogue. */
function cheapArtifact(c: Ctx): ArtifactDefId | null {
  const ids = [...c.content.artifacts.keys()].sort().filter((a) => {
    const def = c.content.artifacts.get(a)!;
    return def.cost > 0 && def.cost <= 5000 && def.slot !== "spellbook"
      && !def.slot.startsWith("warMachine");
  });
  if (ids.length === 0) return null;
  return ids[rollBounded(c, "map.treasure", ids.length)] ?? null;
}

// --------------------------------------------------------------- the sirens

/**
 * The Sirens take their share of the whole army and pay in experience.
 *
 * Every stack loses the same percentage, which is what makes the Sirens a
 * decision rather than a free stop: an army that has just been reinforced is
 * exactly the army it costs most to sail past them with. The classic charges 30% and
 * that is the def's own number.
 *
 * The last creature of a stack is never taken - a stack rounded away to nothing
 * would silently change an army's shape, and a hero left with nothing at all
 * cannot walk - so a one-creature stack sails through free. The experience is
 * the drowned creatures' own `aiValue`, the same rate the Altar pays, because
 * they are the same trade with a different price tag.
 */
function sirens(
  c: Ctx, heroId: HeroId, h: HeroState, o: MapObjectState, pay: Payload,
): VisitOutcome {
  if (playerVisited(c, o, h.owner) && h.visited.includes(o.id)) return DONE;
  const pct = payNum(pay, "lossPct", 30);
  const army = c.s.armies[h.army];
  if (army === undefined || pct <= 0) return DONE;

  let experience = 0;
  let drowned = 0;
  for (let i = 0; i < army.slots.length; i++) {
    const s = c.s.armies[h.army]?.slots[i];
    if (s === null || s === undefined || s.count <= 1) continue;
    const take = Math.trunc((s.count * pct) / 100);
    if (take <= 0) continue;
    const def = c.content.creatures.get(s.def);
    experience += (def?.aiValue ?? 0) * take;
    drowned += take;
    setSlot(c, h.army, i, { def: s.def, count: s.count - take });
  }
  if (drowned === 0) {
    message(c, h.owner, "sirens:nothing");
    return DONE;
  }
  grantExperience(c, heroId, experience);
  message(c, h.owner, `sirens:${String(drowned)}:${String(experience)}`);
  return DONE;
}

// --------------------------------------------------------- the join offer

/**
 * A wandering stack's join, answered.
 *
 * It lives here rather than beside `monster()` in `objects.ts` for the reason
 * `payload.ts` exists: `objects.ts` imports this file for `visitService`, so
 * importing `resolveJoin` back from it closed a cycle -
 * `objects.ts -> services.ts -> objects.ts`. That cycle ran perfectly well the
 * day it was written, which is exactly the danger: an ESM cycle only bites when
 * evaluation order puts a binding in TDZ at the moment it is read, so it is
 * stable until a new top-level const or a reordered import flips it, and then
 * it surfaces as `ReferenceError` at the first battle and reads as a rule bug.
 * Everything below is a leaf from here, so the function simply moves down.
 */
/**
 * The hero pays and the stack falls in behind them.
 *
 * Re-checked on the answer rather than trusted from the prompt: a prompt
 * survives across commands, and the gold can have been spent on a town in
 * between. The stack is taken from the guard army rather than re-derived, so
 * what joins is exactly what was offered.
 */
export function resolveJoin(c: Ctx, heroId: HeroId, objectId: ObjectId, price: number): void {
  const h = c.s.heroes[heroId];
  const o = c.s.objects[objectId];
  if (h === undefined || o === undefined || o.removed || o.guard === null) return;
  const army = c.s.armies[o.guard];
  const slot = army?.slots.find((s) => s !== null && s.count > 0) ?? null;
  if (slot === null) return;

  const pl = c.s.players[h.owner];
  if (price > 0 && (pl?.resources.gold ?? 0) < price) {
    c.events.push({
      type: "Message", player: h.owner, text: `monster:join:poor:${String(price)}`,
    });
    return;
  }
  const mine = c.s.armies[h.army];
  if (mine === undefined || !hasFreeSlotFor(mine, slot.def)) {
    c.events.push({ type: "Message", player: h.owner, text: "monster:join:full" });
    return;
  }
  if (price > 0) addResource(c, h.owner, "gold", -price);
  addCreatures(c, h.army, slot.def, slot.count);
  removeObject(c, objectId);
  c.events.push({
    type: "Message", player: h.owner,
    text: `monster:joined:${slot.def as string}:${String(slot.count)}`,
  });
}

// ------------------------------------------------------------ timed events

/**
 * The classic timed event: a day the calendar reaches, not a cell a hero walks
 * onto.
 *
 * It is carried on a map object like everything else, because that is the only
 * place this kernel has for authored scenario data that has to survive a save
 * (I7 - state is plain data, and `GameState` grows a field only when something
 * cannot be expressed without one). An event object's coordinates are not read
 * by anything here; a scenario parks it wherever it likes, and `blocksMovement`
 * false keeps it out of the way.
 *
 * | key          | meaning                                                   |
 * |--------------|-----------------------------------------------------------|
 * | `service`    | `"event"`                                                  |
 * | `day`        | the absolute day it first fires, 1-based                   |
 * | `repeatDays` | fire again every N days after that; 0 or absent means once |
 * |              | (stride is measured from the LAST firing, held in          |
 * |              | `firedDay`, so a skipped day costs one payment not the     |
 * |              | rhythm - and `day: 1` fires on the first boundary there is)|
 * | `players`    | comma list of player ids; empty means every living kingdom |
 * | `text`       | the message, verbatim                                      |
 *
 * plus the whole `reward.ts` vocabulary, which is applied to each named
 * player's FIRST hero - or, when they have none, to their treasury only, since
 * gold and resources are a kingdom's and experience is a hero's.
 *
 * Nothing in `data/objects` authors one yet: an event object is invisible and
 * every def in this content set must have a drawn sprite, so the def belongs
 * with the map editor that will write them. The rule ships first so that
 * writing one is the only thing left to do.
 */
export function applyTimedEvents(c: Ctx, absoluteDay: number): void {
  for (const id of entityKeys(c.s.objects)) {
    const o = c.s.objects[id]!;
    if (o.removed || o.kind !== "bonus") continue;
    const pay = payloadOf(c, o);
    if (payStr(pay, "service") !== "event") continue;
    if (!firesOn(pay, absoluteDay)) continue;

    const named = payList(pay, "players");
    const text = payStr(pay, "text");
    for (const pid of entityKeys(c.s.players)) {
      const p = c.s.players[pid]!;
      if (p.defeated || p.kind === "none") continue;
      if (named.length > 0 && !named.includes(pid as string)) continue;
      if (text !== "") message(c, pid as PlayerId, text);
      payTo(c, pid as PlayerId, pay);
    }
    // A one-shot event is spent. A repeating one is not, and must not be
    // removed or it would fire once and never again - it records the day
    // instead, which is what the next `firesOn` measures its stride from.
    if (payNum(pay, "repeatDays", 0) <= 0) removeObject(c, o.id);
    else setPayload(c, o.id, { firedDay: absoluteDay });
  }
}

/**
 * Fires today?
 *
 * `day` is the earliest it may fire and `repeatDays` the stride after that.
 * Neither is an exact match on the calendar, and the reason is that a day this
 * function is never called on is a day the event would otherwise be lost:
 *
 *  - the first firing is the first boundary **at or after** `day`. A game
 *    begins on day 1 and `advanceDay` only runs on the way *out* of it, so the
 *    first `absoluteDay` this ever sees is 2 - an event authored for day 1
 *    would never fire at all, and `bld_event` ships `day: 1` as its default.
 *    The same rule covers a save restored past an event's day.
 *  - the stride is measured from the last firing (`firedDay`) and not from
 *    `day`, so a skipped day costs one payment rather than the whole rhythm.
 *    An exact modulo on `(today - day)` loses every subsequent firing the
 *    moment one is missed.
 */
function firesOn(pay: Payload, today: number): boolean {
  const first = payNum(pay, "day", 0);
  if (first <= 0 || today < first) return false;
  const fired = payNum(pay, "firedDay", 0);
  // Never fired: the first boundary AT OR AFTER the authored day, not the
  // authored day exactly. `day: 1` would otherwise never fire at all - a game
  // begins on day 1 and `advanceDay` only runs on the way OUT of it, so the
  // first `absoluteDay` this function ever sees is 2 - and an event authored
  // for a day a restored save has already passed would be lost in the same way.
  if (fired <= 0) return true;
  const stride = payNum(pay, "repeatDays", 0);
  // Measured from the last firing rather than from `day`, so a day the clock
  // skipped costs one payment and not the whole rhythm.
  return stride > 0 && today - fired >= stride;
}

/**
 * Pay one kingdom what the event promises.
 *
 * Resources go to the treasury and everything else needs a hero to receive it,
 * so a kingdom with no hero standing still collects the gold and nothing else.
 * The hero is the first by id, which is stable across a save and is the same
 * order every other per-player sweep in this kernel walks.
 */
function payTo(c: Ctx, p: PlayerId, pay: Payload): void {
  const gold = payNum(pay, "gold", 0);
  if (gold !== 0) addResource(c, p, "gold", gold);
  for (const r of RESOURCES) {
    if (r === "gold") continue;
    const v = payNum(pay, r, 0);
    if (v !== 0) addResource(c, p, r, v);
  }
  for (const hid of entityKeys(c.s.heroes)) {
    if (c.s.heroes[hid]!.owner !== p) continue;
    // `grantReward` would pay the gold a second time, so the treasury half is
    // done above and only the hero half is delegated.
    const heroOnly: Record<string, number | string | boolean | null> = { ...pay };
    heroOnly["gold"] = 0;
    heroOnly["resource"] = "";
    grantReward(c, hid, heroOnly);
    return;
  }
}
